import { enqueueSnackbar } from 'notistack';

import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';

import { useTranslate } from 'src/locales';
import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';
import { TenantType } from 'src/types/enums';
import { FeatureType } from 'src/types/subscription.types';
import { IOrgCandidateListItem, OrganizationCandidateType } from 'src/services/candidates/candidates.types';
import { CommunicationEntityType } from 'src/services/integration/integrations.types';

import Iconify from 'src/components/iconify';
import CustomPopover, { usePopover } from 'src/components/custom-popover';
import { useFeatureFlags } from 'src/sections/billing/usage/hooks/use-feature-flags';
import { useCommunicationActions, buildDialNumber } from 'src/sections/admin/integrations/communications/use-communication-actions';

// ----------------------------------------------------------------------

interface CandidateRowActionsProps {
  row: IOrgCandidateListItem;
  onView: (candidateId: string) => void;
  onAddToJob: (candidateId: string) => void;
  onExportProfile: (candidateId: string) => void;
  onInvite: (candidateId: string) => void;
  onSendEmail: (row: IOrgCandidateListItem) => void;
  onUnlink: (candidateId: string) => void;
  onRemove: (candidateId: string) => void;
}

/**
 * The candidate row kebab, extracted from the old candidate-table-row so it can be dropped into the
 * shared DataList's row-actions slot (JT1-1528). Every action from the previous list is preserved:
 * view, add-to-job, export, invite (unlinked only), send email (feature-gated), copy email/phone,
 * call/SMS (when comms is active — the SMS modal must render at this persistent root so it survives
 * the popover closing), unlink (linked only), remove (unlinked only).
 *
 * Reads the flat IOrgCandidateListItem the lean endpoint returns (not the old candidate_profile
 * nesting), so email/phone/name come straight off the row.
 */
export default function CandidateRowActions({
  row,
  onView,
  onAddToJob,
  onExportProfile,
  onInvite,
  onSendEmail,
  onUnlink,
  onRemove,
}: CandidateRowActionsProps) {
  const { t } = useTranslate();
  const popover = usePopover();
  const tenantType = useOrgTenant();
  const { featureEnabled } = useFeatureFlags();

  const commsName = `${row.first_name ?? ''} ${row.last_name ?? ''}`.trim();
  const commsNumber = row.privileged_access ? buildDialNumber(row.phone_number ?? undefined, row.country_code ?? undefined) : undefined;
  const comms = useCommunicationActions(commsNumber, commsName, { entity_type: CommunicationEntityType.CANDIDATE, entity_id: row.id });

  const copy = (text?: string | null) => {
    popover.onClose();
    if (!text) return;
    navigator.clipboard.writeText(text);
    enqueueSnackbar(t('common.copied_to_clipboard'), { variant: 'success' });
  };

  const item = (icon: string, label: string, onClick: () => void, color?: string) => (
    <MenuItem sx={color ? { color } : undefined} onClick={() => { popover.onClose(); onClick(); }}>
      <Iconify icon={icon} />
      <Typography variant="body2">{label}</Typography>
    </MenuItem>
  );

  return (
    <>
      <IconButton color={popover.open ? 'inherit' : 'default'} onClick={popover.onOpen}>
        <Iconify icon="eva:more-horizontal-fill" />
      </IconButton>

      <CustomPopover open={popover.open} onClose={popover.onClose} arrow="right-top" sx={{ width: 200 }}>
        {item('solar:eye-bold', t('common.view'), () => onView(row.id))}

        {item('mdi:invite', t('common.add_to_job'), () => onAddToJob(row.id))}

        {item('bx:export', t('common.export_profile'), () => onExportProfile(row.id))}

        {(row.type === OrganizationCandidateType.UNLINKED && row.email) &&
          item('lucide:mail-plus', t('common.invite'), () => onInvite(row.id))}

        {(featureEnabled(FeatureType.EMAIL_CONNECTOR_ENABLED) && tenantType !== TenantType.Candidate && row.email) &&
          item('lsicon:email-send-filled', t('common.send_email'), () => onSendEmail(row))}

        {row.email && item('mdi:email-outline', t('candidates.detail.actions.copy_email'), () => copy(row.email))}
        {row.phone_number && item('solar:phone-linear', t('candidates.detail.actions.copy_phone'), () => copy(row.phone_number))}

        {comms.commsActive && (
          <>
            {item('solar:phone-bold', t('integrations.communications.actions.call'), () => comms.call())}
            {item('solar:chat-round-dots-bold', t('integrations.communications.actions.sms'), () => comms.openSms())}
          </>
        )}

        {row.type === OrganizationCandidateType.LINKED &&
          item('mynaui:unlink', t('common.unlink'), () => onUnlink(row.id))}

        {row.type === OrganizationCandidateType.UNLINKED &&
          item('eva:trash-2-outline', t('common.remove'), () => onRemove(row.id), 'error.main')}
      </CustomPopover>

      {comms.smsModal}
    </>
  );
}
